import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BuildingWithConvertedAddresses,
  OffMarketWillingnessEvent,
} from '../utils/types';
import { BuildingMapHandle } from '../HouseMap/BuildingMap';
import { useHorizontalScroll } from '../common/useHorizontalScroll';
import ListingCard from '../ListingCard';
import { ListingCardListing } from '../ListingCard/types';
import ActivityStats from '../common/ActivityStats/ActivityStats';
import { formatStreetLine, hasStreetAddress } from '../utils/addressFormatting';
import { sortPreviewListings } from '../utils/listingOrder';
import { unconfirmedDetailsLabel } from '../ExploreListings/matchDetails';
import type { AreaDemandCard } from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';
import {
  areaInterestToCardListing,
  criteriaPriceLabel,
} from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestCard';
import heartFilledIcon from '../common/icons/heart-filled.png';
import BuildingFrontages from '../common/BuildingFrontages/BuildingFrontages';
import { useSideAddresses } from '../BuildingModal/BuildingView/useSideAddresses';
import { resolvePrimaryAddress } from '../HouseMap/sideAddresses';
import type { AddMyPlaceSeed } from '../AppContent/NewLandingPage/AddMyPlaceFlow/AddMyPlaceFlow';
import type { ListingBuildingGeometry } from '../AppContent/NewLandingPage/AddMyPlaceFlow/resolveListingBuilding';
import './BuildingPreviewCard.scss';

export interface BuildingPreviewCardProps {
  building: BuildingWithConvertedAddresses;
  listings: OffMarketWillingnessEvent[];
  /** Anonymized area interests covering this building, ordered alongside
   *  building buyers after seller listings. */
  areaDemand?: AreaDemandCard[];
  isLoading?: boolean;
  /** Distinguish a filtered-out row from a building with no activity. */
  hasFilteredListings?: boolean;
  buyerCount: number;
  likeCount: number;
  followerCount?: number;
  isLiked?: boolean;
  isDesktop: boolean;
  /** Desktop only: screen point of the map click to anchor the popover at
   *  (initial position before live tracking kicks in). */
  anchor?: { x: number; y: number } | null;
  /** Building lng/lat + map handle: when both are present (desktop), the card
   *  re-projects and follows the building as the map pans/zooms. */
  lngLat?: { lat: number; lng: number } | null;
  mapHandle?: React.RefObject<BuildingMapHandle>;
  /** Reattach movement tracking when the handle's internal map becomes ready. */
  mapReady?: boolean;
  /** The landing page is not on Explore (a building, a listing or a nav view
   *  is open): hide the card and ignore clicks until it is (JAA-445). */
  isCovered?: boolean;
  isLoggedIn?: boolean;
  setLoginModalVisible?: (visible: boolean) => void;
  onClose: () => void;
  /** Expand to the full building view. */
  onExpand: () => void;
  /** Open one specific listing (or area interest) in the single-listing view.
   *  Required: the cards derive their whole click contract from it, so an
   *  omitted handler would silently render them inert and unfocusable. */
  onOpenListing: (listing: ListingCardListing) => void;
  /** Launch the seeded AMP flow for the "Sell here" CTA. */
  onSellHere?: (seed: AddMyPlaceSeed | null) => void;
  onToggleLike?: () => void;
}

const ExpandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M6 3h7v7M13 3 4 12" stroke="#131c20" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path d="M3 3l9 9M12 3l-9 9" stroke="#131c20" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// Inline (not <img src=svg>) so the non-square heart renders crisp, not stretched.
const HeartIcon = () => (
  <svg width="17" height="15" viewBox="0 0 20.1667 17.4167" fill="none" aria-hidden="true">
    <path
      d="M13.5971 0.0326004C12.275 0.167626 11.0384 0.731448 10.0838 1.63211C9.21314 0.816041 8.10917 0.274451 6.91522 0.0800816C5.7228 -0.11281 4.49684 0.0489224 3.40048 0.546005C2.38953 1.01637 1.53562 1.75233 0.934824 2.67081C0.332501 3.59077 0.00924565 4.65613 0.00011128 5.74523C-0.0303854 11.2443 6.21539 15.5131 8.90378 17.0948H8.90226C9.25907 17.3055 9.66771 17.4167 10.0855 17.4167C10.4987 17.4167 10.9028 17.3055 11.2566 17.0962C13.8595 15.5665 19.9389 11.4296 20.1587 6.08061C20.2487 4.39058 19.5732 2.74804 18.3106 1.58471C17.048 0.421381 15.3261 -0.146955 13.5971 0.0326004ZM10.2531 15.4727V15.4712C10.1494 15.5336 10.0183 15.5336 9.91306 15.4712C8.03903 14.3688 1.92751 10.4071 1.95327 5.75697V5.75845C1.96242 4.04765 3.11978 2.5431 4.80472 2.05492C6.48966 1.56826 8.3042 2.21072 9.27236 3.63961C9.4523 3.90521 9.75727 4.06398 10.0851 4.06398C10.4114 4.06398 10.7179 3.90522 10.8978 3.63961C11.5596 2.66921 12.6331 2.03862 13.824 1.9214C15.0149 1.8027 16.1966 2.21074 17.046 3.03129C17.845 3.81917 18.2704 4.89642 18.217 6.00182C18.0203 10.557 12.072 14.4075 10.2514 15.4715L10.2531 15.4727Z"
      fill="#29363d"
    />
  </svg>
);

// The live Mapbox footprint rides on building.geometry — captured from the map
// tiles on a click ('mapbox-tiles') or from a Mapbox-union backend
// ('mapbox-union-v2'). Only those are precise enough for the strict
// on-footprint side-address probe; a legacy/untrusted polygon could be oversized
// and admit a neighbour's address. The EMPTY_POLYGON sentinel (coordinates: [])
// means "not resolved yet" → no footprint.
const TRUSTED_FOOTPRINT_SOURCES = new Set(['mapbox-tiles', 'mapbox-union-v2']);

function trustedFootprint(
  building: BuildingWithConvertedAddresses,
): GeoJSON.Polygon | GeoJSON.MultiPolygon | null {
  if (!TRUSTED_FOOTPRINT_SOURCES.has(building.geometry_source ?? '')) return null;
  const g = building.geometry;
  if (!g || !Array.isArray(g.coordinates) || g.coordinates.length === 0) return null;
  return g as GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

/**
 * On-map building preview opened by clicking a marker or explore card: address
 * + activity + a horizontal row of the building's listings. Expand (or tapping a
 * listing) opens the full building view; on desktop the popover anchors at the
 * map click point, on mobile it docks at the bottom.
 */
const BuildingPreviewCard: React.FC<BuildingPreviewCardProps> = ({
  building,
  listings,
  areaDemand = [],
  isLoading = false,
  hasFilteredListings = false,
  buyerCount,
  likeCount,
  followerCount = 0,
  isLiked = false,
  isDesktop,
  anchor,
  lngLat,
  mapHandle,
  mapReady = false,
  isCovered = false,
  isLoggedIn,
  setLoginModalVisible,
  onClose,
  onExpand,
  onOpenListing,
  onSellHere,
  onToggleLike,
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const scroll = useHorizontalScroll();

  const { addresses: sideAddresses, loading: sideAddressesLoading } =
    useSideAddresses(building, trustedFootprint(building));
  const clicked =
    building.addresses?.[0] ?? building.properties?.addresses?.[0] ?? null;
  const addressObject =
    listings[0]?.address ||
    resolvePrimaryAddress(clicked, sideAddresses, building.centroid);
  const addressLoading = !addressObject && sideAddressesLoading;
  // A district-only map selection (JAA-417): titled by district, never seeds a listing.
  const noStreet = !!addressObject && !hasStreetAddress(addressObject);
  const streetLine = addressObject ? formatStreetLine(addressObject) : '';
  const addressLine = noStreet
    ? building.properties?.name || addressObject?.city || ''
    : streetLine;

  // Build the AMP seed for the "Sell here" CTA, mirroring
  // BuildingView.buildSellSeed. Unlike the building view the card has no
  // separately fetched geometry, so the seed uses building.geometry directly;
  // the AMP publish path re-resolves geometry when it is empty.
  const buildSellSeed = (): AddMyPlaceSeed | null => {
    if (!addressObject || noStreet) return null;
    const center = building.centroid ?? addressObject.coordinates ?? null;
    if (!center) return null;
    const street = [addressObject.street, addressObject.houseNumber]
      .filter(Boolean)
      .join(' ')
      .trim();
    const geometry =
      building.geometry as unknown as ListingBuildingGeometry | null;
    return {
      buildingId: building.id,
      address: street || addressLine || building.properties?.name || '',
      area: addressObject.city || '',
      postal: addressObject.postcode || '',
      center,
      addressObject: {
        country: addressObject.country || '',
        region: addressObject.region || '',
        city: addressObject.city || '',
        postcode: addressObject.postcode || '',
        street: addressObject.street || '',
        houseNumber: addressObject.houseNumber || '',
        coordinates: addressObject.coordinates ?? center,
      },
      geometry,
    };
  };

  /**
   * "Sell here" launches the seeded AMP flow. A district-only building opens
   * it unseeded, at the address step, so the seller types the real address.
   * Falls back to expanding the full building view when no launcher was
   * provided or the address hasn't resolved yet.
   */
  const handleSellClick = () => {
    const seed = buildSellSeed();
    if (onSellHere && (seed || noStreet)) {
      onSellHere(seed);
      return;
    }
    onExpand();
  };

  /**
   * "Buy here" opens the could-buy flow seeded from this building,
   * mirroring "Sell here" beside it. The card is portalled and has no prop path
   * down to the NewLandingPage-owned flow, so it goes through the same
   * 'jaakko:open-could-buy' event the other cross-component entry points use.
   *
   * No login gate here, same as the building view's "Buy here": a logged-out
   * visitor gets to see the whole area-interest flow, and CouldBuyFlow asks them
   * to sign in only when they press "Post your interest". A district-only
   * building (JAA-417) is sent without its id: it has no street to anchor a
   * building-level interest on, so the flow starts an area interest around it.
   */
  const handleBuyClick = () => {
    // Without a building id the flow has nothing to seed from, so fall back to
    // the building page rather than dispatching an event it cannot consume.
    if (!building.id) {
      onExpand();
      return;
    }
    window.dispatchEvent(
      new CustomEvent('jaakko:open-could-buy', {
        detail: {
          buildingId: noStreet ? '' : building.id,
          coordinates: building.centroid ?? addressObject?.coordinates ?? null,
          address: {
            street: addressObject?.street ?? '',
            houseNumber: addressObject?.houseNumber ?? '',
            city: addressObject?.city ?? '',
            country: addressObject?.country ?? '',
          },
        },
      }),
    );
  };

  const ordered = sortPreviewListings<
    OffMarketWillingnessEvent | AreaDemandCard
  >([...listings, ...areaDemand]).sort((a, b) =>
    Number(a.match_status === 'unconfirmed') - Number(b.match_status === 'unconfirmed'),
  );

  const desktopPeek = isDesktop && ordered.length > 1;

  const isEmpty = !isLoading && ordered.length === 0;

  const style: React.CSSProperties | undefined = isDesktop ? { bottom: 'auto' } : undefined;

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    if (!isDesktop) {
      // The same card can survive a breakpoint change. Remove placement styles
      // written on desktop so the mobile CSS can dock it above the screen edge.
      ['left', 'top', 'bottom', 'max-height'].forEach((property) => {
        el.style.removeProperty(property);
      });
      return undefined;
    }
    const map = mapHandle?.current;
    const header = document.querySelector<HTMLElement>('.custom-navbar');
    const panel = document.querySelector<HTMLElement>(
      '.new-landing-page__desktop-explore-info-panel',
    );
    const navPill = document.querySelector<HTMLElement>('.jaakko-nav__pill');
    const desktopCta = document.querySelector<HTMLElement>(
      '.new-landing-page__desktop-explore-bottom [data-map-occluder="cta"]',
    );
    const visibleRect = (element: HTMLElement | null) => {
      const rect = element?.getBoundingClientRect();
      return rect?.width && rect.height ? rect : null;
    };
    const place = () => {
      const pt = (lngLat && map?.projectLngLat(lngLat)) || anchor;
      if (!pt) return;
      // Use the rendered size: the CTA row, wrapped addresses and XL styles
      // can all change the card's dimensions after it first opens.
      const headerBottom = visibleRect(header)?.bottom ?? 80;
      const panelRight = visibleRect(panel)?.right ?? 0;
      const topEdge = Math.max(92, headerBottom + 12);
      const leftEdge = Math.max(460, panelRight + 12);
      const left = Math.max(
        12,
        Math.min(Math.max(pt.x - 180, leftEdge), window.innerWidth - el.offsetWidth - 12),
      );
      const bottomEdge = [navPill, desktopCta].reduce<number>(
        (edge, bottomOccluder) => {
          const rect = visibleRect(bottomOccluder);
          const overlaps =
            rect &&
            left < rect.right + 12 &&
            left + el.offsetWidth > rect.left - 12;
          return overlaps ? Math.min(edge, rect.top - 12) : edge;
        },
        window.innerHeight - 12,
      );
      // A short viewport still needs room for the header and bottom controls.
      // Constrain the card before measuring it so its contents scroll instead
      // of hiding.
      el.style.maxHeight = `${Math.max(0, bottomEdge - topEdge)}px`;
      const top = Math.max(
        topEdge,
        Math.min(pt.y + 14, bottomEdge - el.offsetHeight),
      );
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.bottom = 'auto';
    };
    place();
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    [el, header, panel, navPill, desktopCta].forEach((element) => {
      if (element) observer?.observe(element);
    });
    window.addEventListener('resize', place);
    const unsubscribe = lngLat && map ? map.subscribeMove(place) : undefined;
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
      unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, isLoggedIn, lngLat?.lat, lngLat?.lng, mapHandle, mapReady, anchor]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const interestActions = (
    <div
      className={`building-preview-card__actions${
        isEmpty ? '' : ' building-preview-card__actions--populated'
      }`}
    >
      <button
        type="button"
        className="building-preview-card__cta building-preview-card__cta--sell"
        onClick={handleSellClick}
      >
        {t('building.view.sellHere', 'Sell here')}
      </button>
      <button
        type="button"
        className="building-preview-card__cta building-preview-card__cta--buy"
        onClick={handleBuyClick}
      >
        {t('building.view.buyHere', 'Buy here')}
      </button>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`building-preview-card${
        isDesktop ? ' building-preview-card--desktop' : ' building-preview-card--mobile'
      }${desktopPeek ? ' building-preview-card--peek' : ''}${
        isCovered ? ' building-preview-card--covered' : ''
      }`}
      style={style}
      onClick={stop}
      role="dialog"
      aria-label={addressLine}
      aria-hidden={isCovered}
    >
      <div className="building-preview-card__top">
        <div className="building-preview-card__head">
          <button className="building-preview-card__address" onClick={onExpand}>
            {addressLoading ? (
              <span
                className="building-preview-card__address-skeleton"
                aria-hidden="true"
              />
            ) : (
              addressLine
            )}
            <ExpandIcon />
          </button>
          <div className="building-preview-card__head-actions">
            {onToggleLike && (
              <button
                type="button"
                className={`building-preview-card__icon-btn${
                  isLiked ? ' building-preview-card__icon-btn--active' : ''
                }`}
                aria-pressed={isLiked}
                aria-label={t('listingCard.like', 'Like')}
                onClick={(e) => {
                  stop(e);
                  if (!isLoggedIn) {
                    setLoginModalVisible?.(true);
                    return;
                  }
                  onToggleLike();
                }}
              >
                {isLiked ? (
                  <img
                    className="building-preview-card__icon"
                    src={heartFilledIcon}
                    alt=""
                    aria-hidden="true"
                  />
                ) : (
                  <HeartIcon />
                )}
              </button>
            )}
            <button
              type="button"
              className="building-preview-card__icon-btn"
              aria-label={t('common.close', 'Close')}
              onClick={(e) => {
                stop(e);
                onClose();
              }}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {noStreet && (
          <p className="building-preview-card__no-street">
            {t('building.view.noStreetAddress')}
          </p>
        )}
        <BuildingFrontages building={building} primaryAddress={addressObject} />

        <div className="building-preview-card__activity">
          <ActivityStats
            buyerCount={buyerCount}
            likeCount={likeCount}
            followerCount={followerCount}
            classPrefix="building-preview-card"
          />
        </div>
      </div>

      {isEmpty ? (
        <div className="building-preview-card__empty">
          <p className="building-preview-card__empty-text">
            {hasFilteredListings
              ? t(
                  'building.view.previewNoMatchingListings',
                  'No listings in this building match your filters.',
                )
              : t(
                  'building.view.previewEmptyInterest',
                  'Be the first to show interest in selling or buying in this building.',
                )}
          </p>
          {interestActions}
        </div>
      ) : (
        <div className="building-preview-card__populated">
          <div className="building-preview-card__scroller">
            <div
              ref={scroll.ref}
              className={`building-preview-card__content${
                scroll.dragging ? ' building-preview-card__content--dragging' : ''
              }`}
              {...scroll.handlers}
              onDragStart={(e) => e.preventDefault()}
            >
              {isLoading ? (
                <div className="building-preview-card__skeleton" />
              ) : (
                <>
                  {ordered.map((listing) => {
                    const isArea = 'area_interest_id' in listing;
                    const kind = isArea ? 'buyer' : (
                      listing.match_kind ?? (listing.could_buy && !listing.could_sell ? 'buyer' : 'seller')
                    );
                    const note = listing.match_status === 'unconfirmed'
                      ? unconfirmedDetailsLabel(listing.unconfirmed_fields, t, kind)
                      : '';
                    return <div
                      key={isArea ? `area-${listing.area_interest_id}` : `listing-${listing._id}`}
                      className={`building-preview-card__listing-wrap${note ? ' building-preview-card__listing-wrap--noted' : ''}`}
                    >
                      {isArea ? <ListingCard
                        size="small"
                        type="buyer"
                        listing={
                          areaInterestToCardListing(
                            listing as AreaDemandCard,
                            t,
                            !!listing.isUserOwnEvent,
                          ) as unknown as ListingCardListing
                        }
                        priceText={criteriaPriceLabel(listing as AreaDemandCard) || undefined}
                        priceTextShort={
                          criteriaPriceLabel(listing as AreaDemandCard, true) || undefined
                        }
                        onOpen={onOpenListing}
                        isLoggedIn={isLoggedIn}
                        setLoginModalVisible={setLoginModalVisible}
                      /> : (
                      <ListingCard
                        size="small"
                        type={kind}
                        listing={listing as unknown as ListingCardListing}
                        onOpen={onOpenListing}
                        isLoggedIn={isLoggedIn}
                        setLoginModalVisible={setLoginModalVisible}
                        hideEmptyCompactTitle
                      />
                    )}
                      {note && <p className="building-preview-card__listing-note">{note}</p>}
                    </div>;
                  })}
                </>
              )}
            </div>
            <span
              className="building-preview-card__fade building-preview-card__fade--left"
              data-visible={scroll.canLeft ? 'true' : 'false'}
              aria-hidden="true"
            />
            <span
              className="building-preview-card__fade building-preview-card__fade--right"
              data-visible={scroll.canRight ? 'true' : 'false'}
              aria-hidden="true"
            />
          </div>
          {interestActions}
        </div>
      )}
    </div>
  );
};

export default BuildingPreviewCard;
